import React, { useEffect, useState, useRef } from "react";
import { apiFetch } from "../utils/apiClient";
import ReserveQuantityEditor from './ReserveQuantityEditor';
const money = (minor) => `${(Number(minor || 0) / 100).toLocaleString("fr-FR")} F`;
const labels = { issue: "Mise en réserve", redeem: "Utilisation", adjust_issue: "Réajustement", reverse_issue: "Annulation de mise en réserve", reverse_redeem: "Recrédit après annulation" };

export default function ReservesDashboard() {
  const [enabled, setEnabled] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState({ items: [], total: 0, pageCount: 0 });
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const detailRequest = useRef(0);
  const [editing,setEditing] = useState(false);
  useEffect(() => {
    let current = true;
    apiFetch("/api/reserves/settings", { includeBearer: true }).then(async (response) => {
      if (response.ok && current) { const settings = await response.json(); setEnabled(settings.ready); }
    }).catch(() => {});
    return () => { current = false; };
  }, []);
  useEffect(() => {
    if (!enabled) return;
    detailRequest.current += 1; setDetail(null); setEditing(false);
    let current = true;
    setBusy(true); setError("");
    apiFetch(`/api/reserves?${new URLSearchParams({ search, page, pageSize })}`, { includeBearer: true }).then(async (response) => {
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Chargement impossible");
      if (current) setData(payload);
    }).catch((failure) => { if (current) setError(failure.message); }).finally(() => { if (current) setBusy(false); });
    return () => { current = false; };
  }, [enabled, search, page, pageSize]);
  if (!enabled) return null;
  const open = async (id) => {
    const request = ++detailRequest.current;
    setEditing(false);
    if (detail?.id === id) { setDetail(null); return; }
    setError("");
    try {
      const response = await apiFetch(`/api/reserves/${encodeURIComponent(id)}`, { includeBearer: true });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Détail indisponible");
      if (request === detailRequest.current) setDetail(payload);
    } catch (failure) { setError(failure.message); }
  };
  return <section className="my-6 rounded-2xl border border-white/15 bg-slate-900 p-5 text-white" aria-label="Tableau des articles en réserve">
    <h2 className="text-xl font-semibold">Articles en réserve</h2>
    <p className="mt-2 text-sm text-white/60">Montants déjà payés. Leur utilisation n’est pas un nouvel encaissement.</p>
    <div className="my-4 flex flex-wrap gap-3">
      <input aria-label="Rechercher une réserve" placeholder="Facture ou référence de réserve" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} className="rounded-lg bg-slate-800 p-3 text-white" />
      <label>Éléments par page <select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }} className="rounded bg-slate-800 p-2">{[10, 25, 50, 100].map((size) => <option key={size}>{size}</option>)}</select></label>
    </div>
    {error && <p role="alert" className="text-red-300">{error}</p>}
    {busy ? <p role="status">Chargement…</p> : data.items.map((item) => <div key={item.id}>
      <button type="button" aria-expanded={detail?.id === item.id} onClick={() => open(item.id)} className="my-2 flex w-full justify-between gap-4 rounded-lg border border-white/15 p-3 text-left"><span>{item.public_reference || item.origin_reference}{item.customer_name ? ` · ${item.customer_name}` : ""}</span><span>Solde : {money(item.balance_minor)}</span></button>
      {detail?.id === item.id && <div className="mb-4 rounded-xl border border-amber-300/30 p-4">
        <h3 className="font-semibold">Réserve {detail.public_reference || detail.origin_reference}</h3><p>Solde actuel : {money(detail.balance_minor)}</p>
        {(detail.lines || []).map(line => <p key={line.itemReference} className="mt-2">{line.label} · {line.quantity} en réserve · {money(line.netMinor)}</p>)}
        {detail.canAdjust && !editing && <button type="button" onClick={()=>setEditing(true)} className="mt-3 rounded border border-amber-300/50 px-3 py-2">Réajuster les quantités</button>}
        {editing && detail.canAdjust && <ReserveQuantityEditor detail={detail} onClose={()=>setEditing(false)} onSaved={async()=>{
          const request = detailRequest.current;
          const response=await apiFetch(`/api/reserves/${encodeURIComponent(detail.id)}`,{includeBearer:true});
          const payload=await response.json(); if(!response.ok) throw new Error(payload.error || 'Détail indisponible');
          if(request===detailRequest.current) { setDetail(payload); setEditing(false); setData(previous=>({...previous,items:previous.items.map(row=>row.id===detail.id ? {...row,balance_minor:payload.balance_minor} : row)})); }
        }} />}
        {detail.events.map(event => <p key={event.id} className="mt-2 text-sm text-white/60">{new Date(event.created_at).toLocaleString("fr-FR")} · {labels[event.kind]} · {money(event.delta_minor)}{event.reason ? ` · ${event.reason}` : ""}</p>)}
      </div>}
    </div>)}
    {!busy && !data.items.length && <p>Aucune réserve trouvée.</p>}
    <div className="mt-4 flex items-center gap-4"><button type="button" disabled={page <= 1 || busy} onClick={() => setPage(page - 1)}>Précédent</button><span>{page} / {Math.max(1, data.pageCount)} · {data.total} réserves</span><button type="button" disabled={page >= data.pageCount || busy} onClick={() => setPage(page + 1)}>Suivant</button></div>
  </section>;
}
